import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const SHORTER_DATE_MESSAGE =
  "Expiration cannot be earlier than listed. Mark the item out of stock instead.";

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13, bgcolor: "#fff" },
  "& .MuiInputBase-input": { py: 0.75 },
  "& .MuiFormLabel-root": { fontSize: 12 },
} as const;

const actionBtnSx = {
  minWidth: "auto",
  px: 1,
  py: 0.25,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  lineHeight: 1.4,
} as const;

const editBtnSx = {
  minWidth: "auto",
  px: 1,
  py: 0.375,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  lineHeight: 1.2,
  borderRadius: "6px",
  flexShrink: 0,
  color: "text.primary",
  borderColor: "rgba(15, 23, 42, 0.18)",
  bgcolor: "#fff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  "& .MuiButton-startIcon": { mr: 0.5, ml: -0.125 },
  "&:hover": {
    bgcolor: "grey.50",
    borderColor: "rgba(15, 23, 42, 0.28)",
  },
} as const;

const editPanelSx = {
  mt: 0.5,
  pl: 1,
  py: 0.75,
  pr: 0.75,
  borderLeft: "2px solid",
  borderColor: "rgba(15, 23, 42, 0.1)",
  bgcolor: "rgba(15, 23, 42, 0.02)",
  borderRadius: "0 6px 6px 0",
} as const;

export interface ConfirmationUnitFieldsProps {
  lotNumber: string;
  expDate: Dayjs;
  originalLotNumber: string;
  originalExpMonth: Dayjs;
  disabled?: boolean;
  onLotChange: (lotNumber: string) => void;
  onExpChange: (expDate: string) => void;
  onInvalidChange?: (invalid: boolean) => void;
}

export const ConfirmationUnitFields: React.FC<ConfirmationUnitFieldsProps> = ({
  lotNumber,
  expDate,
  originalLotNumber,
  originalExpMonth,
  disabled = false,
  onLotChange,
  onExpChange,
  onInvalidChange,
}) => {
  const [editingLot, setEditingLot] = useState(false);
  const [editingExp, setEditingExp] = useState(false);
  const [lotDraft, setLotDraft] = useState(lotNumber);
  const [expDraft, setExpDraft] = useState(expDate);
  const [expKeyboardMode, setExpKeyboardMode] = useState(false);

  const expDateKey = expDate.format("YYYY-MM-DD");

  useEffect(() => {
    if (!editingLot) setLotDraft(lotNumber);
  }, [lotNumber, editingLot]);

  useEffect(() => {
    if (!editingExp) {
      setExpDraft(expDate);
      setExpKeyboardMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expDateKey, editingExp]);

  const savedExpDate = expDate;
  const savedLotNumber = lotNumber;
  const dateTooEarly = !!expDraft && expDraft.startOf("month").isBefore(originalExpMonth);
  const hasLotEdit = lotNumber !== originalLotNumber;
  const hasExpEdit = expDate.startOf("month").valueOf() !== originalExpMonth.valueOf();

  const renderEditButton = (onClick: () => void) => (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
      sx={editBtnSx}
    >
      Edit
    </Button>
  );

  const renderFieldActions = (onSave: () => void, onCancel: () => void, saveDisabled = false) => (
    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={onSave}
        disabled={saveDisabled}
        sx={{
          ...actionBtnSx,
          bgcolor: "grey.800",
          color: "#fff",
          "&:hover": { bgcolor: "grey.900" },
          "&.Mui-disabled": { bgcolor: "grey.300", color: "rgba(255, 255, 255, 0.9)" },
        }}
      >
        Save
      </Button>
      <Button size="small" variant="text" onClick={onCancel} sx={{ ...actionBtnSx, color: "text.secondary" }}>
        Cancel
      </Button>
    </Box>
  );

  const openExpEdit = () => {
    setExpDraft(savedExpDate);
    setExpKeyboardMode(false);
    setEditingExp(true);
    setEditingLot(false);
  };

  const applyExpDate = (date: Dayjs | null) => {
    if (!date || !date.isValid()) return;

    const next = date.startOf("month");
    setExpDraft(next);

    if (next.isBefore(originalExpMonth)) {
      onInvalidChange?.(true);
      return;
    }

    onExpChange(next.format("YYYY-MM-DD"));
    onInvalidChange?.(false);
    setExpKeyboardMode(false);
    setEditingExp(false);
  };

  const saveExpEdit = () => {
    applyExpDate(expDraft);
  };

  const cancelExpEdit = () => {
    setExpDraft(savedExpDate);
    setExpKeyboardMode(false);
    onInvalidChange?.(false);
    setEditingExp(false);
  };

  const handleExpAccept = (date: Dayjs | null) => {
    // Calendar selection applies immediately; typed values wait for Save.
    if (expKeyboardMode) {
      if (date?.isValid()) setExpDraft(date.startOf("month"));
      return;
    }
    applyExpDate(date);
  };

  const openLotEdit = () => {
    setLotDraft(savedLotNumber);
    setEditingLot(true);
    setEditingExp(false);
  };

  const saveLotEdit = () => {
    onLotChange(lotDraft);
    onInvalidChange?.(false);
    setEditingLot(false);
  };

  const cancelLotEdit = () => {
    setLotDraft(savedLotNumber);
    setEditingLot(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "stretch",
        gap: { xs: 1, sm: 0 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, pr: { sm: 1.25 } }}>
        {!editingExp ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minHeight: 28 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Expiration{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 400 }}>
                {savedExpDate.format("MM/YYYY")}
              </Box>
              {hasExpEdit && (
                <Box component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                  edited
                </Box>
              )}
            </Typography>
            {!disabled && renderEditButton(openExpEdit)}
          </Box>
        ) : (
          <Box sx={editPanelSx}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Expiration"
                value={expDraft}
                onChange={(date) => setExpDraft(date || originalExpMonth)}
                onAccept={handleExpAccept}
                onOpen={() => setExpKeyboardMode(false)}
                views={["month", "year"]}
                format="MM/YYYY"
                minDate={originalExpMonth}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: dateTooEarly,
                    sx: inputSx,
                    onKeyDown: (event) => {
                      // Ignore modifier-only / navigation keys so Tab/arrows don't force Save mode.
                      if (event.key.length === 1 || event.key === "Backspace" || event.key === "Delete") {
                        setExpKeyboardMode(true);
                      }
                      if (event.key === "Enter" && expKeyboardMode) {
                        event.preventDefault();
                        saveExpEdit();
                      }
                    },
                    onPaste: () => setExpKeyboardMode(true),
                  },
                }}
              />
            </LocalizationProvider>
            {dateTooEarly && (
              <Typography sx={{ fontSize: 11, color: "error.main", mt: 0.5, lineHeight: 1.35 }}>
                {SHORTER_DATE_MESSAGE}
              </Typography>
            )}
            {expKeyboardMode ? (
              renderFieldActions(saveExpEdit, cancelExpEdit, dateTooEarly || !expDraft?.isValid())
            ) : (
              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                <Button size="small" variant="text" onClick={cancelExpEdit} sx={{ ...actionBtnSx, color: "text.secondary" }}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider
        orientation="vertical"
        flexItem
        sx={{
          display: { xs: "none", sm: "block" },
          borderColor: "rgba(15, 23, 42, 0.1)",
          mx: 0.5,
        }}
      />
      <Divider
        sx={{
          display: { xs: "block", sm: "none" },
          borderColor: "rgba(15, 23, 42, 0.1)",
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0, pl: { sm: 1.25 } }}>
        {!editingLot ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minHeight: 28 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 0 }}>
              Lot{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 400, wordBreak: "break-all" }}>
                {savedLotNumber}
              </Box>
              {hasLotEdit && (
                <Box component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                  edited
                </Box>
              )}
            </Typography>
            {!disabled && renderEditButton(openLotEdit)}
          </Box>
        ) : (
          <Box sx={editPanelSx}>
            <TextField
              label="Lot number"
              size="small"
              fullWidth
              value={lotDraft}
              onChange={(e) => setLotDraft(e.target.value)}
              sx={inputSx}
            />
            {renderFieldActions(saveLotEdit, cancelLotEdit)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConfirmationUnitFields;
